import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import axios from "axios";
import type {
  AllocateCreditsResponse,
  BalanceContent,
  BalanceHistory,
  CreditPurchaseResponse,
  DeductCreditsResponse,
  PaymentMethodsResponse,
  TeamMemberCreditsResponse,
  TeamMemberCreditsUpdatePayload,
  UpdateDefaultPaymentMethodResponse,
} from "./types";

const BALANCE_URL = `${backend_url}/api/stripe/management/balance/`;
const TRANSACTIONS_URL = `${backend_url}/api/stripe/management/transactions/`;
const PURCHASE_URL = `${backend_url}/api/stripe/management/purchase/`;
const DEDUCT_URL = `${backend_url}/api/stripe/management/usage/`;
const ALLOCATE_URL = `${backend_url}/api/stripe/management/allocate/`;
const PAYMENT_METHODS_URL = `${backend_url}/api/stripe/management/payment/methods/`;
const TEAM_CREDITS_URL = `${backend_url}/api/stripe/management/team-member/credits/`;

/**
 * Fetches the Stripe balance of the business.
 *
 * @returns {Promise<BalanceResponse>}
 * An object containing the business balance, allocated credits, and team members (if applicable).
 * @throws Will throw an error if the request fails.
 */
export const getBalance: () => Promise<BalanceContent> = async () => {
  try {
    const response = await axios.get(BALANCE_URL, {
      ...AXIOS_CONFIG,
    });

    // Check if the response is successful
    return response.data.content; // Return the actual data
  } catch (error) {
    console.error("Error fetching balance:", error);
    throw error; // Re-throw the error for React Query to handle
  }
};

/**
 * Fetches the balance history of transactions from the Stripe management API.
 *
 * @param {number} page - The page number for pagination (default: 1).
 * @param {number} page_size - The number of transactions per page (default: 10).
 * @returns {Promise<BalanceHistory[]>} A promise that resolves to an array of BalanceHistory objects.
 * @throws Will throw an error if the request fails.
 */
export const getBalanceHistory = async (
  page: number = 1,
  page_size: number = 10
): Promise<BalanceHistory[]> => {
  try {
    const response = await axios.get(TRANSACTIONS_URL, {
      ...AXIOS_CONFIG,
      params: { page, page_size },
    });

    // Check if the response is successful
    if (response.data.message === "success") {
      return response.data.content.results; // Return the transaction history
    } else {
      throw new Error(
        response.data.message ?? "Failed to fetch transaction history"
      );
    }
  } catch (error) {
    console.error("Error fetching transaction history:", error);
    throw error;
  }
};
/**
 * Initiates a Stripe Checkout session to purchase additional credits.
 *
 * @param {number} amount - The number of credits to purchase.
 * @param {string} [paymentMethodId] - Optional payment method ID.
 * @returns {Promise<CreditPurchaseResponse>} A promise that resolves to the checkout URL.
 * @throws Will throw an error if the request fails.
 * @deprecated This function is deprecated and will be removed in a future release.
 * Credit purchases should be handled through subscription upgrades via the unified checkout API.
 */
export const purchaseCredits = async (
  amount: number,
  paymentMethodId?: string // Optional payment method ID
): Promise<CreditPurchaseResponse> => {
  console.warn(
    "[DEPRECATED] purchaseCredits is deprecated and will be removed in a future release. " +
      "Credit purchases should be handled through subscription upgrades via the unified checkout API."
  );
  try {
    const payload = {
      amount,
      payment_method: paymentMethodId,
    };

    const response = await axios.post(
      PURCHASE_URL,
      payload, // Updated payload
      {
        ...AXIOS_CONFIG,
      }
    );

    return {
      checkout_url: response.data.checkout_url,
    };
  } catch (error) {
    console.error("Error purchasing credits:", error);
    throw error;
  }
};

/**
 * Deducts a specified number of credits from the business's wallet.
 *
 * @param {number} amount - The number of credits to deduct.
 * @param {string} feature_name - (Optional) A description for the deduction.
 * @param {string} client_id - (Optional) The ID of the client associated with the deduction.
 * @param {number} tax_year - (Optional) The tax year for which the deduction is being made.
 * @returns {Promise<DeductCreditsResponse>} A promise that resolves to the new balance and transaction details.
 * @throws Will throw an error if the request fails.
 */
export const deductCredits = async (
  amount: number,
  feature_name?: string,
  client_id?: string,
  tax_year?: number,
  requires_confirmation?: boolean,
  hide_confirmation?: boolean
): Promise<DeductCreditsResponse> => {
  try {
    const response = await axios.post(
      DEDUCT_URL,
      {
        amount,
        feature_name,
        requires_confirmation,
        hide_confirmation,
        feature_data: { client_id, tax_year },
      },
      {
        ...AXIOS_CONFIG,
      }
    );

    return {
      newBusinessBalance: response.data.content?.newBusinessBalance,
      job: response.data.content?.job,
      requiresConfirmation: response.data.content?.requiresConfirmation,
    };
  } catch (error) {
    console.error("Error deducting credits:", error);
    throw error;
  }
};

/**
 * Allocates a specified amount of credits from the business's unallocated credit balance to a team member.
 *
 * @param {string} userId - The target team member's user identifier.
 * @param {number} amount - The number of credits to allocate.
 * @returns {Promise<AllocateCreditsResponse>} A promise that resolves to the allocated amount and updated balances.
 * @throws Will throw an error if the request fails.
 */
export const allocateCredits = async (
  userId: string,
  amount: number
): Promise<AllocateCreditsResponse> => {
  try {
    const response = await axios.post(
      ALLOCATE_URL,
      { user_id: userId, amount },
      {
        ...AXIOS_CONFIG,
      }
    );

    return {
      allocatedAmount: response.data.content.allocatedAmount,
      newUserBalance: response.data.content.newUserBalance,
      newBusinessBalance: response.data.content.newBusinessBalance,
    };
  } catch (error) {
    console.error("Error allocating credits:", error);
    throw error;
  }
};

/**
 * Fetches the list of payment methods from the Stripe management API.
 *
 * @returns {Promise<PaymentMethodsResponse>} A promise that resolves to the list of payment methods.
 * @throws Will throw an error if the request fails.
 */
export const getPaymentMethods = async (): Promise<PaymentMethodsResponse> => {
  try {
    const response = await axios.get(PAYMENT_METHODS_URL, {
      ...AXIOS_CONFIG,
    });

    // Check if the response is successful
    if (response.data.message === "success") {
      return {
        message: response.data.message,
        content: response.data.content,
      };
    } else {
      throw new Error(
        response.data.message ?? "Failed to fetch payment methods"
      );
    }
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    // Return an empty content array instead of throwing an error
    // This prevents 500 error on PurchaseCreditsModal.tsx
    return {
      message: "success",
      content: [], // Return an empty array of payment methods
    };
  }
};

/**
 * Updates the default payment method for the business.
 *
 * @param {string} paymentMethodId - The ID of the payment method to set as default.
 * @returns {Promise<UpdateDefaultPaymentMethodResponse>} A promise that resolves to the updated default payment method.
 * @throws Will throw an error if the request fails.
 */
export const updateDefaultPaymentMethod = async (
  paymentMethodId: string
): Promise<UpdateDefaultPaymentMethodResponse> => {
  try {
    const response = await axios.post(
      PAYMENT_METHODS_URL,
      { default_payment_method_id: paymentMethodId },
      {
        ...AXIOS_CONFIG,
      }
    );

    const isSuccess =
      response.status === 200 ||
      response.data?.status_code === 200 ||
      response.data?.statusCode === 200;

    if (isSuccess) {
      return {
        message: response.data.message,
        content: response.data.content,
      };
    } else {
      throw new Error(
        response.data.message ?? "Failed to update default payment method"
      );
    }
  } catch (error) {
    console.error("Error updating default payment method:", error);
    throw error;
  }
};

/**
 * Sends a request to allocate credits to a team member.
 *
 * @returns {Promise<{ message: string }>} A promise that resolves to a success message.
 * @throws Will throw an error if the request fails.
 */
export const requestCredits = async (): Promise<{ message: string }> => {
  try {
    const response = await axios.post(
      BALANCE_URL,
      {}, // No body is needed for this request
      {
        ...AXIOS_CONFIG,
      }
    );

    return {
      message: response.data.message,
    };
  } catch (error) {
    console.error("Error requesting credits:", error);
    throw error;
  }
};

/**
 * Updates team member credits.
 *
 * @param {TeamMemberCreditsUpdatePayload} payload - The payload containing target_user_id, credit_request, and business_use.
 * @returns {Promise<TeamMemberCreditsResponse>} A promise that resolves to the updated team member credits.
 * @throws Will throw an error if the request fails.
 */
export const updateTeamMemberCredits = async (
  payload: TeamMemberCreditsUpdatePayload
): Promise<TeamMemberCreditsResponse> => {
  try {
    const response = await axios.post(TEAM_CREDITS_URL, payload, {
      ...AXIOS_CONFIG,
    });

    return {
      message: response.data.message,
      content: response.data.content,
    };
  } catch (error) {
    console.error("Error updating team member credits:", error);
    throw error;
  }
};
